/**
 * Byte-level readers for Ingest (spec--pdfmarkuptool-image-markup 4.1 step 1): the format from the magic bytes
 * (never `File.type`, DIM-1428), the stored frame size from the header, and the few EXIF tags Ingest needs --
 * DateTimeOriginal / OffsetTimeOriginal (capturedAt) and Orientation (to size the decode; the browser rotates).
 *
 * The EXIF reader is NOT a general parser: it follows IFD0 and the Exif IFD pointer only. The GPS IFD is never
 * followed and nothing location-related is read or returned. Every read is bounds-checked against the APP1
 * segment: garbled metadata gives `{}`, never a throw.
 */

export type ImageKind = 'jpeg' | 'png' | 'gif' | 'webp' | 'heic' | 'avif' | 'unknown';

const HEIC_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs']);
const AVIF_BRANDS = new Set(['avif', 'avis']);

function asciiAt(b: Uint8Array, at: number, len: number): string {
    if (b.length < at + len) return '';
    return String.fromCharCode(...b.subarray(at, at + len));
}

/** The image format from the file's first bytes (never `File.type`). Pass at least the first 32 bytes. */
export function sniffImage(b: Uint8Array): ImageKind {
    if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'jpeg';
    if (b.length >= 8 && b[0] === 0x89 && asciiAt(b, 1, 3) === 'PNG' && b[4] === 0x0d && b[5] === 0x0a) return 'png';
    if (asciiAt(b, 0, 4) === 'GIF8') return 'gif';
    if (asciiAt(b, 0, 4) === 'RIFF' && asciiAt(b, 8, 4) === 'WEBP') return 'webp';
    if (asciiAt(b, 4, 4) === 'ftyp') {
        const size = ((b[0]! << 24) | (b[1]! << 16) | (b[2]! << 8) | b[3]!) >>> 0;
        const end = Math.min(b.length, Math.max(16, size));
        const brands = [asciiAt(b, 8, 4)];
        for (let at = 16; at + 4 <= end; at += 4) brands.push(asciiAt(b, at, 4));
        // The major brand decides; a generic major brand (mif1/msf1) falls back to the compatible list.
        if (HEIC_BRANDS.has(brands[0]!)) return 'heic';
        if (AVIF_BRANDS.has(brands[0]!)) return 'avif';
        if (brands.some((x) => AVIF_BRANDS.has(x))) return 'avif';
        if (brands.some((x) => HEIC_BRANDS.has(x))) return 'heic';
    }
    return 'unknown';
}

/** 4.1 step 1: JPEG, PNG, GIF (first frame) and HEIC (where the browser decodes it). */
export type IngestKind = 'jpeg' | 'png' | 'gif' | 'heic';

export function isIngestible(kind: ImageKind): kind is IngestKind {
    return kind === 'jpeg' || kind === 'png' || kind === 'gif' || kind === 'heic';
}

export interface Size { w: number; h: number }

/** A JPEG's header segments: [marker, payload start, payload end) up to SOS, stopping at anything malformed. */
function* jpegSegments(b: Uint8Array): Generator<{ marker: number; start: number; end: number }> {
    if (sniffImage(b) !== 'jpeg') return;
    let at = 2;
    while (at + 4 <= b.length) {
        if (b[at] !== 0xff) return;
        const marker = b[at + 1]!;
        if (marker === 0xff) { at += 1; continue; } // fill byte
        if (marker === 0xd9 || marker === 0xda) return; // EOI / start of scan: the header is over
        if ((marker >= 0xd0 && marker <= 0xd7) || marker === 0x01) { at += 2; continue; } // no length
        const len = (b[at + 2]! << 8) | b[at + 3]!;
        if (len < 2) return;
        yield { marker, start: at + 4, end: Math.min(b.length, at + 2 + len) };
        at += 2 + len;
    }
}

const isSof = (m: number) => m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc;

/** The stored (un-oriented) frame size from the header; null when the kind has none Ingest reads (HEIC) or it is cut. */
export function imageSize(b: Uint8Array): Size | null {
    const kind = sniffImage(b);
    if (kind === 'jpeg') {
        for (const s of jpegSegments(b)) {
            if (isSof(s.marker) && s.end - s.start >= 5) {
                const h = (b[s.start + 1]! << 8) | b[s.start + 2]!;
                const w = (b[s.start + 3]! << 8) | b[s.start + 4]!;
                return w > 0 && h > 0 ? { w, h } : null;
            }
        }
        return null;
    }
    if (kind === 'png') {
        if (b.length < 24 || asciiAt(b, 12, 4) !== 'IHDR') return null;
        const u32 = (p: number) => ((b[p]! << 24) | (b[p + 1]! << 16) | (b[p + 2]! << 8) | b[p + 3]!) >>> 0;
        const w = u32(16);
        const h = u32(20);
        return w > 0 && h > 0 ? { w, h } : null;
    }
    if (kind === 'gif') {
        if (b.length < 10) return null;
        const w = b[6]! | (b[7]! << 8);
        const h = b[8]! | (b[9]! << 8);
        return w > 0 && h > 0 ? { w, h } : null;
    }
    return null;
}

export interface ExifTags {
    /** IFD0 0x0112; used only to size the decode (the browser applies it). */
    orientation?: number;
    /** Exif IFD 0x9003, "YYYY:MM:DD HH:MM:SS". */
    dateTimeOriginal?: string;
    /** Exif IFD 0x9011, "+HH:MM". */
    offsetTimeOriginal?: string;
}

const MAX_ENTRIES = 512;

/** The tags of {@link ExifTags} from a JPEG's APP1 Exif segment; `{}` when absent or garbled. */
export function readExif(b: Uint8Array): ExifTags {
    for (const s of jpegSegments(b)) {
        if (s.marker === 0xe1 && asciiAt(b, s.start, 6) === 'Exif\0\0') {
            try {
                return readTiff(b, s.start + 6, s.end);
            } catch {
                return {};
            }
        }
    }
    return {};
}

class OutOfRange extends Error {}

function readTiff(b: Uint8Array, tiff: number, end: number): ExifTags {
    const order = asciiAt(b, tiff, 2);
    if (order !== 'II' && order !== 'MM') return {};
    const le = order === 'II';
    const need = (p: number, n: number) => { if (!(p >= tiff && p + n <= end)) throw new OutOfRange(); };
    const u16 = (p: number) => { need(p, 2); return le ? b[p]! | (b[p + 1]! << 8) : (b[p]! << 8) | b[p + 1]!; };
    const u32 = (p: number) => {
        need(p, 4);
        return (le ? (b[p]! | (b[p + 1]! << 8) | (b[p + 2]! << 16) | (b[p + 3]! << 24))
            : ((b[p]! << 24) | (b[p + 1]! << 16) | (b[p + 2]! << 8) | b[p + 3]!)) >>> 0;
    };
    const ascii = (entry: number): string | undefined => {
        if (u16(entry + 2) !== 2) return undefined;
        const count = u32(entry + 4);
        if (count === 0 || count > 64) return undefined;
        const at = count <= 4 ? entry + 8 : tiff + u32(entry + 8);
        need(at, count);
        let s = '';
        for (let i = 0; i < count && b[at + i] !== 0; i++) s += String.fromCharCode(b[at + i]!);
        return s;
    };
    const entries = (ifd: number): number[] => {
        const n = u16(ifd);
        if (n > MAX_ENTRIES) throw new OutOfRange();
        need(ifd + 2, 12 * n);
        return Array.from({ length: n }, (_, i) => ifd + 2 + 12 * i);
    };

    const out: ExifTags = {};
    let exifIfd: number | undefined;
    for (const e of entries(tiff + u32(tiff + 4))) {
        const tag = u16(e);
        if (tag === 0x0112 && u16(e + 2) === 3) out.orientation = u16(e + 8);
        if (tag === 0x8769) exifIfd = tiff + u32(e + 8);
        // 0x8825 (GPS IFD pointer) is deliberately never followed.
    }
    if (exifIfd !== undefined) {
        try {
            for (const e of entries(exifIfd)) {
                const tag = u16(e);
                if (tag === 0x9003) out.dateTimeOriginal = ascii(e);
                if (tag === 0x9011) out.offsetTimeOriginal = ascii(e);
            }
        } catch (err) {
            if (!(err instanceof OutOfRange)) throw err;
            delete out.dateTimeOriginal;
            delete out.offsetTimeOriginal;
        }
    }
    for (const k of Object.keys(out) as Array<keyof ExifTags>) if (out[k] === undefined) delete out[k];
    return out;
}

const DATE_RE = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/;
const OFFSET_RE = /^([+-])(\d{2}):(\d{2})$/;

/**
 * An EXIF DateTimeOriginal as ms epoch. With a valid OffsetTimeOriginal it is the exact instant; without one the
 * wall clock is read as this device's local time (the usual case: the photo was taken where the iPad is).
 * Blank, zero and impossible dates -> undefined.
 */
export function parseExifDateTime(dateTime: string, offset?: string): number | undefined {
    const m = DATE_RE.exec(dateTime);
    if (!m) return undefined;
    const [y, mo, d, h, mi, s] = m.slice(1).map(Number) as [number, number, number, number, number, number];
    if (y < 1900 || mo < 1 || mo > 12 || d < 1 || h > 23 || mi > 59 || s > 59) return undefined;
    const days = new Date(Date.UTC(y, mo, 0)).getUTCDate();
    if (d > days) return undefined;
    const o = offset ? OFFSET_RE.exec(offset) : null;
    if (o) {
        const minutes = Number(o[2]) * 60 + Number(o[3]);
        return Date.UTC(y, mo - 1, d, h, mi, s) - (o[1] === '-' ? -1 : 1) * minutes * 60_000;
    }
    return new Date(y, mo - 1, d, h, mi, s).getTime();
}

/** The capture time an original file's EXIF records, if any. */
export function exifCaptureMs(b: Uint8Array): number | undefined {
    const t = readExif(b);
    return t.dateTimeOriginal ? parseExifDateTime(t.dateTimeOriginal, t.offsetTimeOriginal) : undefined;
}
